import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    InputAdornment,
    Divider,
    Paper,
    Grid,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FinanceService } from '../../../api/services/financeService';
import { formatCurrency, useCurrency } from '../../../utils/currency';
import { toast } from '../../../utils/toast';

/**
 * Cash Drop to Safe Modal
 * Endpoint: POST /api/v1/finance/shifts/cash-drop
 * Permission: finance.declare_till
 * 
 * Calculations:
 * availableCashInTill = openingFloat + cashInflows - cashRefunds - cashExpenses - existingCashDrops
 * Safety Validation: If requestedDropAmount > availableCashInTill, reject with 400:
 * "Insufficient physical cash in drawer for drop. Available drawer cash is ₹X, cannot drop ₹Y."
 * On Success: remainingDrawerCash = availableCashInTill - requestedDropAmount
 */
export default function RecordCashDropModal({ open, onClose, shift, onSuccess, tenantId: propTenantId }) {
    const { currency } = useCurrency();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = propTenantId || authData.tenantId || '1';

    const [activeShifts, setActiveShifts] = useState([]);
    const [selectedShiftId, setSelectedShiftId] = useState('');
    const [selectedShift, setSelectedShift] = useState(null);
    const [shiftLiquidity, setShiftLiquidity] = useState(null);
    const [dropAmount, setDropAmount] = useState('');
    const [destination, setDestination] = useState('Main Hotel Safe');
    const [remarks, setRemarks] = useState('');
    const [witness, setWitness] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingShiftData, setLoadingShiftData] = useState(false);

    // Load active shifts on open if no specific shift passed
    useEffect(() => {
        if (open) {
            setDropAmount('');
            setRemarks('');
            setWitness('');
            setDestination('Main Hotel Safe');
            setShiftLiquidity(null);

            if (shift?.id) {
                setSelectedShiftId(shift.id);
                setSelectedShift(shift);
            } else {
                fetchActiveShifts();
            }
        }
    }, [open, shift]);

    // Live sync shift liquidity summary from server if shift ID present
    useEffect(() => {
        if (open && selectedShiftId) {
            FinanceService.getShiftLiquiditySummary(selectedShiftId, tenantId)
                .then(data => {
                    if (data) setShiftLiquidity(data);
                })
                .catch(() => {
                    // Gracefully fallback to shift object fields
                });
        }
    }, [open, selectedShiftId, tenantId]);

    const fetchActiveShifts = async () => {
        setLoadingShiftData(true);
        try {
            const res = await FinanceService.getOpenCashierShifts(tenantId);
            const list = res?.shifts || (Array.isArray(res) ? res : []);
            setActiveShifts(list);
            if (list.length > 0) {
                setSelectedShiftId(list[0].id);
                setSelectedShift(list[0]);
            }
        } catch (error) {
            console.error('Failed to load active shifts for cash drop:', error);
        } finally {
            setLoadingShiftData(false);
        }
    };

    const handleShiftSelect = (shiftId) => {
        setSelectedShiftId(shiftId);
        const found = activeShifts.find(s => s.id === shiftId);
        setSelectedShift(found || null);
    };

    // Calculate Available Cash In Till
    // availableCashInTill = openingFloat + cashInflows - cashRefunds - cashExpenses - existingCashDrops
    const openingFloat = Number(
        shiftLiquidity?.openingFloat ??
        selectedShift?.openingFloat ?? 
        0
    );
    const cashInflows = Number(
        shiftLiquidity?.inflows?.byTender?.Cash ??
        shiftLiquidity?.inflows?.cash ??
        selectedShift?.cashInflows ??
        selectedShift?.inflows?.byTender?.Cash ??
        selectedShift?.expectedTotals?.Cash ??
        (selectedShift?.payments?.find(p => p.method === 'Cash')?.expected || 0)
    );
    const cashRefunds = Number(
        shiftLiquidity?.outflows?.refunds ??
        shiftLiquidity?.outflows?.byTender?.Refunds ??
        shiftLiquidity?.refunds ??
        selectedShift?.cashRefunds ?? 
        selectedShift?.outflows?.byTender?.Refunds ?? 
        0
    );
    const cashExpenses = Number(
        (shiftLiquidity?.outflows?.pettyCash !== undefined || shiftLiquidity?.outflows?.otherExpenses !== undefined)
            ? Number(shiftLiquidity?.outflows?.pettyCash || 0) + Number(shiftLiquidity?.outflows?.otherExpenses || 0)
            : (shiftLiquidity?.outflows?.byTender?.PettyCash ??
               shiftLiquidity?.pettyCash ??
               selectedShift?.cashExpenses ?? 
               selectedShift?.outflows?.byTender?.PettyCash ?? 
               0)
    );
    const existingCashDrops = Number(
        shiftLiquidity?.outflows?.cashDrops ??
        shiftLiquidity?.cashDrops ??
        shiftLiquidity?.totalCashDrops ??
        selectedShift?.existingCashDrops ?? 
        selectedShift?.cashDrops ?? 
        0
    );

    const calculatedAvailable = openingFloat + cashInflows - cashRefunds - cashExpenses - existingCashDrops;
    const availableCashInTill = Math.max(
        0,
        Number(shiftLiquidity?.availableCashInTill ?? shiftLiquidity?.expectedPhysicalCash ?? calculatedAvailable)
    );

    const requestedDropAmount = parseFloat(dropAmount) || 0;
    const isOverdrop = requestedDropAmount > availableCashInTill;
    const remainingDrawerCash = Math.max(0, availableCashInTill - requestedDropAmount);

    const currSym = currency === 'AED' ? 'AED ' : '₹';
    const validationErrorMessage = `Insufficient physical cash in drawer for drop. Available drawer cash is ${currSym}${availableCashInTill.toLocaleString('en-IN')}, cannot drop ${currSym}${requestedDropAmount.toLocaleString('en-IN')}.`;

    const handleSubmit = async (e) => {
        e?.preventDefault();

        if (!selectedShiftId) {
            toast.error('Please select an active cashier shift.');
            return;
        }

        if (requestedDropAmount <= 0) {
            toast.error('Please enter a valid cash drop amount.');
            return;
        }

        // Safety Validation check before dispatching
        if (isOverdrop) {
            toast.error(validationErrorMessage);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                shiftId: selectedShiftId,
                amount: requestedDropAmount,
                destination: destination,
                witness: witness.trim() || undefined,
                remarks: remarks.trim() || undefined
            };

            const response = await FinanceService.createCashDrop(tenantId, payload);
            const remainingFromResponse = response?.remainingDrawerCash ?? remainingDrawerCash;

            toast.success(
                `Cash drop of ${formatCurrency(requestedDropAmount, currency)} to ${destination} recorded successfully! Remaining drawer cash: ${formatCurrency(remainingFromResponse, currency)}.`
            );

            if (typeof onSuccess === 'function') {
                onSuccess({
                    ...response,
                    requestedDropAmount,
                    remainingDrawerCash: remainingFromResponse
                });
            }
            onClose();
        } catch (error) {
            const errorMsg = 
                error.response?.data?.message || 
                error.response?.data?.error || 
                error.message || 
                'Failed to record cash drop.';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LocalAtmIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ color: '#0f172a', lineHeight: 1.2 }}>
                            Cash Drop to Safe
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Transfer physical cash from drawer till to secure drop safe
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: '#f1f5f9' }}>
                {loadingShiftData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: '#16a34a' }} />
                    </Box>
                ) : (
                    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {/* Shift Selector if multiple active */}
                        {!shift?.id && activeShifts.length > 1 && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Cashier Shift</InputLabel>
                                <Select
                                    value={selectedShiftId}
                                    label="Cashier Shift"
                                    onChange={(e) => handleShiftSelect(e.target.value)}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {activeShifts.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.outlet || s.role || 'Front desk'} · {s.user?.name || s.name || 'Cashier'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Till Balance Summary Box */}
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 2, 
                                borderRadius: 2.5, 
                                bgcolor: '#f8fafc', 
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" fontWeight="700" sx={{ color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Active Till Physical Cash Balance
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {selectedShift?.outlet || selectedShift?.role || 'Active Shift'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <Typography variant="h4" fontWeight="800" sx={{ color: '#0f172a' }}>
                                    {formatCurrency(availableCashInTill, currency)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 600 }}>
                                    Maximum Allowed Drop
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

                            <Grid container spacing={1}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Opening Float</Typography>
                                    <Typography variant="body2" fontWeight="600">{formatCurrency(openingFloat, currency)}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Cash Inflows</Typography>
                                    <Typography variant="body2" fontWeight="600" color="#16a34a">+{formatCurrency(cashInflows, currency)}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Refunds/Outflows</Typography>
                                    <Typography variant="body2" fontWeight="600" color="#dc2626">-{formatCurrency(cashRefunds + cashExpenses, currency)}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Prior Drops</Typography>
                                    <Typography variant="body2" fontWeight="600" color="#2563eb">-{formatCurrency(existingCashDrops, currency)}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Drop Amount Input */}
                        <Box>
                            <TextField
                                fullWidth
                                label="Cash Drop Amount"
                                type="number"
                                placeholder="0.00"
                                value={dropAmount}
                                error={isOverdrop}
                                onChange={(e) => setDropAmount(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography fontWeight="700" color="primary.main">
                                                {currency === 'AED' ? 'AED' : '₹'}
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                helperText={
                                    isOverdrop 
                                        ? validationErrorMessage
                                        : `Enter amount to transfer to ${destination}.`
                                }
                            />
                        </Box>

                        {/* Safety Validation Alert if Exceeded */}
                        {isOverdrop && (
                            <Alert 
                                severity="error" 
                                icon={<ErrorOutlineIcon />}
                                sx={{ borderRadius: 2 }}
                            >
                                <Typography variant="body2" fontWeight="700">
                                    Insufficient Cash in Drawer
                                </Typography>
                                <Typography variant="caption">
                                    {validationErrorMessage}
                                </Typography>
                            </Alert>
                        )}

                        {/* Success Preview Banner */}
                        {!isOverdrop && requestedDropAmount > 0 && (
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    bgcolor: '#f0fdf4', 
                                    border: '1px solid #bbf7d0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 600 }}>
                                        Remaining Drawer Cash After Drop:
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#15803d' }}>
                                    {formatCurrency(remainingDrawerCash, currency)}
                                </Typography>
                            </Paper>
                        )}

                        {/* Destination & Remarks */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Drop Destination</InputLabel>
                                    <Select
                                        value={destination}
                                        label="Drop Destination"
                                        onChange={(e) => setDestination(e.target.value)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <MenuItem value="Main Hotel Safe">Main Hotel Safe</MenuItem>
                                        <MenuItem value="Front Office Safe">Front Office Safe</MenuItem>
                                        <MenuItem value="Accounts Safe">Accounts Safe</MenuItem>
                                        <MenuItem value="Bank Deposit Drop Box">Bank Deposit Drop Box</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Witness / Staff Initial"
                                    placeholder="e.g. Duty Manager"
                                    value={witness}
                                    onChange={(e) => setWitness(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            size="small"
                            label="Notes / Remarks"
                            placeholder="Optional reason for safe drop..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button 
                    onClick={onClose} 
                    disabled={isSubmitting}
                    sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit}
                    disabled={isSubmitting || isOverdrop || requestedDropAmount <= 0}
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <ShieldOutlinedIcon />}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        fontWeight: 700,
                        px: 3,
                        bgcolor: '#16a34a',
                        '&:hover': { bgcolor: '#15803d' },
                        boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
                    }}
                >
                    {isSubmitting ? 'Transferring...' : `Confirm Drop (${formatCurrency(requestedDropAmount, currency)})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
